import React, { Fragment } from 'react'
import { string, object, bool } from 'prop-types'
import styled, { createGlobalStyle } from 'styled-components'
import { rem } from 'polished'
import { Heading, Box, Flex } from 'rebass'
import { Link, navigate } from 'gatsby'
import buildImageUrl from '../../../lib/media-url-maker'
import { media, CategoryBadge, Grid } from '../../utils/style'
import typography from '../../utils/typography'
import IconAuthor from '../../vectors/IconAuthor'
import IconMapPinFlat from '../../vectors/IconMapPinFlat'
import moment from 'moment'
import IconDownload from '../../vectors/IconDownload'

const GlobalStyles = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`

const Author = styled(Grid)`
  font-size: 15px;
  position: absolute;
  left: 0;
  bottom: ${rem(-34)};
  padding-bottom: 0;
  ${media.MediumScreenUp`
    bottom: ${rem(-68)};
  `};
  svg {
    margin-right: 13px;
    margin-bottom: -2px;
    opacity: 0.5;
  }
`

const ArticleTitle = styled(Heading)`
  font-size: 1.4rem;
  margin-top: ${typography.rhythm(0.5)};
  ${media.LargeScreenUp`
    font-size: 2.4rem;
    margin-top: ${typography.rhythm(1)};
  `};

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 85%;
    left: 0;
    width: 55px;
    height: 2px;
    background: white;
    ${media.LargeScreenUp`
      width: 68px;
    `};
    ${media.MediumScreenUp`
      top: 100%;
    `};
  }
`

const ArticleSubtitle = styled(Heading)`
  color: white;
`

const ContentHeader = styled(Box)`
  position: relative;
  cursor: ${(props) => (props.islink ? 'pointer' : 'initial')};
  overflow: hidden;
  height: auto;

  ${media.MediumScreenUp`
    min-height: 500px;
  `}

  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    width: 100%;
    background-image: url(${(props) =>
      buildImageUrl({ resource: props.bg, width: 500 })});

    @media screen and (min-width: 500px) {
      background-image: url(${(props) =>
        buildImageUrl({ resource: props.bg, width: 700 })});
    }
    @media screen and (min-width: 700px) {
      background-image: url(${(props) =>
        buildImageUrl({ resource: props.bg, width: 900 })});
    }
    @media screen and (min-width: 900px) {
      background-image: url(${(props) =>
        buildImageUrl({ resource: props.bg, width: 1170 })});
    }

    background-size: cover;
    background-repeat: no-repeat;
    background-position: center 25%;
    transition: transform 12.8s ${(props) => props.theme.ease.ease_out_circ};
    transform: ${(props) =>
      props.islink ? 'scale(1.05) translateZ(0)' : 'scale(1)'};
  }

  &:hover::before {
    ${media.LargeScreenUp`
    transform: scale(1) translateZ(0);
    `}
  }

  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.48;
    background-image: linear-gradient(
      -90deg,
      rgba(0, 0, 0, 0) 49%,
      #001130 77%
    );
    z-index: 0;
  }
`

const ContentHeaderSummary = styled(Box)`
  color: white;
  z-index: 1;
  position: relative;
  ${media.LargeScreenUp`
    max-width: 32em;
 `};

  a {
    color: white;
    text-decoration: none;
  }
`
const ContentHeaderDetails = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${(props) => props.theme.colors.grey_op65};
  z-index: 1;
  align-items: stretch;
  flex-wrap: wrap;
`

const ContentHeaderLocation = styled(Box)`
  flex-grow: 1;
  width: 100%;
  display: flex;
  line-height: 1.3;
  color: ${(props) => props.theme.colors.white};
  font-weight: ${(props) => props.theme.weights[1]};
  font-size: ${(props) => rem(props.theme.fontSizes[3])};

  ${media.LargeScreenUp`
    font-size: ${(props) => rem(props.theme.fontSizes[6])};
 `};

  a {
    color: ${(props) => props.theme.colors.white};
    text-decoration: none;

    &:hover {
      color: ${(props) => props.theme.colors.white};
    }
  }
`

const ContentHeaderCTA = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.colors.white};
  text-transform: uppercase;
  font-weight: ${(props) => props.theme.weights[1]};
  text-decoration: none;
  letter-spacing: 0.12em;
  padding: 10px 40px;
  background-color: ${(props) =>
    props.primary ? props.theme.colors.blue : props.theme.colors.grey_light};
  flex-grow: 1;

  &:hover {
    path {
      fill: ${(props) => props.theme.colors.grey};
    }
  }

  ${media.LargeScreenUp`
    padding: 30px 40px;
  `}
`

const LocationIcon = styled(IconMapPinFlat)`
  height: 20px;
  width: 26px;
  transform: translate(-5px, 0);
  margin-right: 5px;

  path {
    fill: ${(props) => props.theme.colors.grey_light};
  }

  ellipse {
    fill: ${(props) => props.theme.colors.grey};
  }

  ${media.LargeScreenUp`
    width: 43px;
    height: 30px;
    transform: translate(-5px, 0);
  `}
`

const StyledIconDownload = styled(IconDownload)`
  margin-right: 5px;
  margin-top: 2px;
  path {
    transition: fill 0.5s ${(props) => props.theme.ease.ease_out_expo};
  }
`

const DateTime = styled.time`
  font-size: 14px;
  color: ${(props) => props.theme.colors.white};
  font-weight: ${(props) => props.theme.weights[1]};
  position: relative;
  top: 2px;
`

class ContentArticleHeader extends React.PureComponent {
  constructor(props) {
    super(props)
    this._linkto = this._linkto.bind(this)
    this.calendarLink = `${process.env.GATSBY_API_URL}/calendar-download?articleId=${props.articleId}`
  }

  _linkto() {
    if (this.props.path) {
      navigate(this.props.path)
    }
  }

  _getTitleColour() {
    const colour = this.props.colour

    if (colour === '' || colour === 'blue') {
      return
    }

    if (colour === 'dark') {
      return '#111111'
    }

    if (colour === 'light') {
      return '#ffffff'
    }

    return colour
  }

  render() {
    const titleColour = this._getTitleColour()

    return (
      <ContentHeader
        as="header"
        role="banner"
        pt={[30, 50, null, '12vh']}
        pb={this.props.isEvent ? [220, 140, '25vh'] : [65, 140, null, '25vh']}
        bg={this.props.bg}
        islink={this.props.path}
        onClick={this._linkto}
      >
        <GlobalStyles />
        <ContentHeaderSummary width={[10 / 12, 3 / 6]} ml={['8%', '10%']}>
          {!!(this.props.category && this.props.category.length > 0) && (
            <CategoryBadge ml={0} mt={0} mb={10} mr={2} px={10} py={'4px'}>
              {this.props.category}
            </CategoryBadge>
          )}
          {this.props.date && (
            <DateTime dateTime={this.props.date}>
              {moment(this.props.date).format('HH:mm - dddd, D MMMM YYYY')}
            </DateTime>
          )}
          {this.props.author && (
            <Author pb={5}>
              <IconAuthor /> {this.props.author}
            </Author>
          )}
          <ArticleTitle
            color={titleColour}
            as="h2"
            pb={this.props.subtitle ? 16 : 48}
          >
            {this.props.path && (
              <Link to={this.props.path}>{this.props.title}</Link>
            )}
            {!this.props.path && this.props.title}
          </ArticleTitle>
          {this.props.subtitle && (
            <ArticleSubtitle fontSize={[3, 6]} pb={32}>
              {this.props.subtitle}
            </ArticleSubtitle>
          )}
        </ContentHeaderSummary>
        {this.props.isEvent && (
          <ContentHeaderDetails>
            <ContentHeaderLocation
              width={['100%', '100%', '100%', '40%']}
              py={[15, 15, 30]}
              pr={[10]}
              ml={['8%', '10%']}
            >
              {this.props.location.label && (
                <Fragment>
                  <LocationIcon />
                  <a href={this.props.location.url}>
                    {this.props.location.label}
                  </a>
                </Fragment>
              )}
            </ContentHeaderLocation>
            {this.props.showCalendarLink && (
              <ContentHeaderCTA href={this.calendarLink}>
                <StyledIconDownload />
                <span>Add to Calendar</span>
              </ContentHeaderCTA>
            )}
            {this.props.showRegisterLink && (
              <ContentHeaderCTA href="#formTop" primary>
                {this.props.ctaLabel || 'Register for Event'}
              </ContentHeaderCTA>
            )}
          </ContentHeaderDetails>
        )}
      </ContentHeader>
    )
  }
}

ContentArticleHeader.defaultProps = {
  title: 'no title',
  category: null,
  author: '',
  bg: '',
  date: '',
  path: null,
  showCalendarLink: true,
  showRegisterLink: true
}
ContentArticleHeader.propTypes = {
  title: string,
  colour: string,
  category: string,
  author: string,
  bg: string,
  date: string,
  path: string,
  location: object,
  ctaLabel: string,
  showCalendarLink: bool,
  showRegisterLink: bool
}

export default ContentArticleHeader
